import { VariableValue, Visualization } from '../../../core/models';
import { StoreLite } from '@mailok/store-lite';
import { shareReplay } from 'rxjs/operators';
import {
    buildVisualizationDescription,
    getNumberByPaintingType,
    getPaintingTypeByNumber,
    PaintingType
} from '../../utils';

export interface VisualizationState {
    variable: VariableValue | null;
    paintingType: PaintingType;
    min: string;
    max: string;
    workingWidth: string;
    toneQuantity: string;
    name: string;
    visualization: Visualization | null;
}

const initialState = (): VisualizationState => ({
    variable: null,
    paintingType: PaintingType.GREEN_RED,
    min: '',
    max: '',
    workingWidth: '',
    toneQuantity: '',
    name: '',
    visualization: null
});

export default class VisualizationStore extends StoreLite<VisualizationState> {
    variable$ = this.select<VariableValue | null>(store => store.variable);
    paintingType$ = this.select<PaintingType>(store => store.paintingType).pipe(shareReplay(1));
    min$ = this.select<string>(store => store.min);
    max$ = this.select<string>(store => store.max);
    workingWidth$ = this.select<string>(store => store.workingWidth);
    toneQuantity$ = this.select<string>(store => store.toneQuantity);
    unit$ = this.select<string>(store => (store.variable && store.variable.unit ? store.variable.unit : ''));
    name$ = this.select<string>(store => store.name);
    constructor() {
        super(initialState());
    }

    buildVisualization(
        variable: VariableValue | null,
        paintingType: PaintingType,
        min: string,
        max: string,
        working_width: string,
        tone_quantity: string,
        name: string
    ): Visualization | null {
        if (variable) {
            return {
                id: '',
                variable: variable,
                painting_type: getNumberByPaintingType(paintingType),
                min: +min,
                max: +max,
                working_width: +working_width,
                tone_quantity: +tone_quantity,
                name
            };
        } else {
            return null;
        }
    }

    updateVisualization(visualization: Visualization | null): void {
        if (visualization !== null) {
            const paintingType = getPaintingTypeByNumber(visualization.painting_type);
            this.update({
                variable: visualization.variable,
                paintingType: paintingType,
                name: visualization.name,
                visualization: visualization,
                min: `${visualization.min}`,
                max: `${visualization.max}`,
                toneQuantity: `${visualization.tone_quantity}`,
                workingWidth: `${visualization.working_width}`
            });
        } else {
            this.update(initialState());
        }
    }

    getStateSnapshot(): VisualizationState {
        return this.selectSnapshot<VisualizationState>(store => store);
    }

    updateVariable(variable: VariableValue): void {
        const state = this.getStateSnapshot();
        const name = buildVisualizationDescription(variable, state.paintingType);
        this.update({
            variable,
            name,
            visualization: this.buildVisualization(
                variable,
                state.paintingType,
                state.min,
                state.max,
                state.workingWidth,
                state.toneQuantity,
                name
            )
        });
    }

    updatePaintingType(paintingType: PaintingType): void {
        const state = this.getStateSnapshot();
        const name = buildVisualizationDescription(state.variable, paintingType);
        this.update({
            paintingType,
            name,
            visualization: this.buildVisualization(
                state.variable,
                paintingType,
                state.min,
                state.max,
                state.workingWidth,
                state.toneQuantity,
                name
            )
        });
    }

    updateMin(min: string) {
        const state = this.getStateSnapshot();
        this.update({
            min,
            visualization: this.buildVisualization(
                state.variable,
                state.paintingType,
                min,
                state.max,
                state.workingWidth,
                state.toneQuantity,
                state.name
            )
        });
    }

    updateMax(max: string) {
        const state = this.getStateSnapshot();
        this.update({
            max,
            visualization: this.buildVisualization(
                state.variable,
                state.paintingType,
                state.min,
                max,
                state.workingWidth,
                state.toneQuantity,
                state.name
            )
        });
    }

    updateWorkingWidth(workingWidth: string) {
        const state = this.getStateSnapshot();
        this.update({
            workingWidth,
            visualization: this.buildVisualization(
                state.variable,
                state.paintingType,
                state.min,
                state.max,
                workingWidth,
                state.toneQuantity,
                state.name
            )
        });
    }

    updateToneQuantity(toneQuantity: string) {
        const state = this.getStateSnapshot();
        this.update({
            toneQuantity,
            visualization: this.buildVisualization(
                state.variable,
                state.paintingType,
                state.min,
                state.max,
                state.workingWidth,
                toneQuantity,
                state.name
            )
        });
    }

    reset() {
        const state = this.getStateSnapshot();
        const name = buildVisualizationDescription(state.variable, PaintingType.GREEN_RED);
        this.update({
            paintingType: PaintingType.GREEN_RED,
            min: '',
            max: '',
            workingWidth: '',
            toneQuantity: '',
            name,
            visualization: this.buildVisualization(state.variable, PaintingType.GREEN_RED, '', '', '', '', name)
        });
    }
}
